import { useState } from "react";
import { Divider, Stack } from "@bookingcom/bui-react";
import { DateRangePickerContentQuickSelects } from "./DateRangePickerContentQuickSelects";
import {
  DateRangePickerContentCalendar,
  type DateRangePickerContentCalendarProps,
} from "./DateRangePickerContentCalendar";
import { DateRangePickerFooter } from "./DateRangePickerFooter";
import {
  noOfDaysToRecentPeriod,
  getDateWithOffset,
  isSameDate,
  recentPeriodToNoOfDays,
  getDaysBetween,
} from "../../../utils/date";
import type { DateRange, RecentPeriod } from "../../../types/date";
import styles from "../DateRangePicker.module.css";

type DateRangePickerContentProps = {
  dateRange: DateRange;
  quickSelect?: RecentPeriod;
  onSubmit: (dateRange: DateRange, quickSelect?: RecentPeriod) => void;
};

export const DateRangePickerContent = ({ dateRange, quickSelect, onSubmit }: DateRangePickerContentProps) => {
  const [state, setState] = useState<{ dateRange: Partial<DateRange>; quickSelect?: RecentPeriod }>({
    dateRange,
    quickSelect,
  });

  const handleQuickSelectChange = (quickSelect: RecentPeriod) => {
    // Compute the start date and end date from quickSelect
    const noOfDays = recentPeriodToNoOfDays(quickSelect);
    const endDate = getDateWithOffset(new Date(), -1);
    const startDate = getDateWithOffset(endDate, -(noOfDays - 1));

    setState((prevState) => ({
      ...prevState,
      dateRange: { startDate, endDate },
      quickSelect,
    }));
  };

  const handleDateRangeChange: DateRangePickerContentCalendarProps["onDateRangeChange"] = ({ startDate, endDate }) => {
    let quickSelect: RecentPeriod | undefined;

    // Compute quickSelect when a date range is provided and the end date is yesterday
    if (startDate && endDate && isSameDate(endDate, getDateWithOffset(new Date(), -1))) {
      const noOfDays = getDaysBetween(startDate, endDate);
      quickSelect = noOfDaysToRecentPeriod(noOfDays);
    }

    setState((prevState) => ({
      ...prevState,
      dateRange: { startDate, endDate },
      quickSelect,
    }));
  };

  const handleSubmit = () => {
    const { startDate, endDate } = state.dateRange;

    if (startDate && endDate) {
      onSubmit({ startDate, endDate }, state.quickSelect);
    }
  };

  return (
    <Stack direction="row" gap={0} wrap="nowrap">
      <Stack.Item className={styles.quickSelectsContainer}>
        <DateRangePickerContentQuickSelects
          quickSelect={state.quickSelect}
          onQuickSelectChange={handleQuickSelectChange}
        />
      </Stack.Item>
      <Stack.Item className={styles.calendarContainer}>
        <Stack>
          <DateRangePickerContentCalendar dateRange={state.dateRange} onDateRangeChange={handleDateRangeChange} />
          <Divider />
          <DateRangePickerFooter dateRange={state.dateRange} onSubmit={handleSubmit} />
        </Stack>
      </Stack.Item>
    </Stack>
  );
};
